<template>
  <div
    ref="elementRef"
    :class="roundedCornersClasses"
  >
    <div
      class="relative"
      :class="[
        usesPercentHeight && 'flex min-h-0 flex-col',
        {
          '<md:o:h-$h <md:overflow-hidden': usesPercentHeightSmMd,
          'o:lg:h-$h-lg lg:overflow-hidden': usesPercentHeightLg,
          'o:h-$h': usesPercentHeightSmMd && usesPercentHeightLg,
          'o:<lg:h-$h': usesPercentHeightSmMd && !usesPercentHeightLg,
        },
      ]"
      :style="percentHeightStyle"
    >
      <div
        v-if="parallaxOffset"
        v-style="{ pb: contentPane }"
        class="relative h-full grid-area-stack"
        :class="{ 'min-h-0 flex-1': usesPercentHeight }"
      >
        <div class="absolute-0 flex items-center overflow-hidden">
          <div ref="parallaxRef" class="w-full" :style="{ height: `calc(100% + ${parallaxTranslate})` }">
            <base-picture
              v-if="responsiveMedia.images"
              v-bind="responsiveMedia.images"
              class="full"
              :fetchpriority
              fit="cover"
              :lazy
              :position="objectPosition"
            />
            <vf-video
              v-else-if="responsiveMedia.video"
              v-bind="responsiveMedia.video"
              ref="videoRef"
              :autoplay="!lazy"
              class="full"
              :directives="{ 'v-style': { objectPosition } }"
              fit="cover"
              loop
              :meta="responsiveMedia.video.sm || responsiveMedia.video.meta"
            />
          </div>
          <cms-base-gradient
            v-if="gradientStyle && gradientStyle !== 'off'"
            v-bind="{ gradientTone, gradientStyle, gradientGrid, gradientBreakpoints }"
            position="bottom"
          />
          <base-link
            v-if="mediaLink"
            aria-hidden="true"
            class="absolute-0"
            :data-cta="mediaLink.delocalizedTracking"
            tabindex="-1"
            :target="mediaLink.targetAttribute"
            :to="mediaLink.url"
            @click="handlePromotionClick('img')"
          />
          <cms-base-caption v-if="hasCaption" v-bind="captionProps" />
        </div>
      </div>

      <div
        v-else
        class="relative flex grid-area-stack"
        :class="{ 'min-h-0 flex-1': usesPercentHeight }"
      >
        <cms-base-caption v-if="hasCaption" v-bind="captionProps" />
        <base-picture
          v-if="responsiveMedia.images"
          v-bind="responsiveMedia.images"
          class="full"
          :fetchpriority
          fit="cover"
          :lazy
          :position="objectPosition"
        />
        <vf-video
          v-else-if="responsiveMedia.video"
          v-bind="responsiveMedia.video"
          ref="videoRef"
          :autoplay="!lazy"
          class="w-full"
          :directives="{ 'v-style': { objectPosition } }"
          fit="cover"
          loop
          :meta="responsiveMedia.video.sm || responsiveMedia.video.meta"
        />
        <cms-base-gradient
          v-if="gradientStyle && gradientStyle !== 'off'"
          v-bind="{ gradientTone, gradientStyle, gradientGrid, gradientBreakpoints }"
          position="bottom"
        />
        <base-link
          v-if="mediaLink"
          aria-hidden="true"
          class="absolute-0"
          :data-cta="mediaLink.delocalizedTracking"
          tabindex="-1"
          :target="mediaLink.targetAttribute"
          :to="mediaLink.url"
          @click="handlePromotionClick('img')"
        />
      </div>
      <div class="relative grid" :class="[brandClasses.grid, { 'shrink-0': usesPercentHeight }]">
        <div
          v-style:bg="backgroundColor"
          class="z-1 f-col items-start gap-6 py-6 md:absolute md:bottom-0 md:mx-10 md:max-w-120 md:px-6"
          :class="[
            contentAlignment,
            alignmentLarge === 'left' ? 'left-0' : 'right-0',
            { 'px-6': backgroundColor },
          ]"
        >
          <div class="w-full f-col gap-4">
            <cms-base-eyebrow
              class="mb-2"
              :eyebrow-logo-responsive
              :eyebrow-logo-width-responsive
              :eyebrow-text
              :eyebrow-text-color
              :eyebrow-text-html
              :eyebrow-text-style
              :position="{ sm: textAlignmentSmall, md: textAlignmentLarge }"
            />
            <div class="f-col gap-2">
              <p v-if="overline" v-style:c="overlineColor" :class="overlineStyle" data-test-id="cms-banner-overline">
                {{ overline }}
              </p>
              <component
                :is="titleTag"
                v-if="title"
                v-style:c="titleColor"
                :class="[titleStyle, { 'sr-only': hideTitle }]"
                data-test-id="cms-banner-title"
              >
                <base-link
                  v-if="mediaLink"
                  :data-cta="mediaLink.delocalizedTracking"
                  :target="mediaLink.targetAttribute"
                  :to="mediaLink.url"
                  @click="handlePromotionClick('title')"
                >
                  <cms-plain-text :content field-name="title" />
                </base-link>
                <template v-else>
                  <cms-plain-text :content field-name="title" />
                </template>
              </component>
              <p
                v-if="subtitle"
                v-style:c="subtitleColor"
                :class="subtitleStyle"
                data-test-id="cms-banner-text"
              >
                <cms-plain-text :content field-name="subtitle" />
              </p>
              <cms-rich-text v-if="richText" v-style:c="richTextColor || subtitleColor" class="pointer-within" :content="{ richText }" />
            </div>
          </div>
          <div class="f-col gap-4" style="justify-content: inherit">
            <cms-shared-cta-group
              v-if="linkType !== 'No-CTA' && targets.length"
              :class="contentAlignment"
              :cta-type
              :equal-target-size
              layout="pointer-within f-col gap-4 wrap md:flex-row"
              :multilink-style
              :multilink-title
              style="justify-content: inherit"
              :targets
              :text-link-style="getTextLinkStyles(content)"
              @click="handlePromotionClick(`cta${$event + 1}`)"
            />
            <cms-rich-text
              v-if="richTextBelowTargets"
              v-style:c="richTextBelowTargetsColor || subtitleColor"
              :content="{ richText: richTextBelowTargets }"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { SectionProvider } from '#content/contexts/section'
import type { BannerOverlayContent } from '#types/components/cms/banner-overlay'
import type { TextAlignment } from '#types/components/cms/text-alignment'
import { DynamicFieldsKey } from '#content/contexts/dynamic-fields'
import { SectionContextKey } from '#content/contexts/section'

const { content, firstElement, index, isPartOfCollection, parentHeadingLevel } = defineProps<{
  content: BannerOverlayContent
  firstElement?: boolean
  index?: number
  isPartOfCollection?: boolean
  parentHeadingLevel?: number
}>()

const cms = useAppConfig().components.cms
const { brandClasses } = cms.bannerOverlay
const { componentCorners } = cms.section

const {
  alignmentLarge,
  backgroundColor,
  componentPercentHeight,
  countdown,
  ctaType,
  equalTargetSize,
  eyebrowLogoResponsive,
  eyebrowLogoWidthResponsive,
  eyebrowText,
  eyebrowTextHtml,
  eyebrowTextColor,
  eyebrowTextStyle,
  gradientBreakpoints,
  gradientGrid,
  gradientStyle,
  gradientTone,
  hideTitle,
  linkType,
  media,
  mediaTarget,
  multilinkStyle,
  multilinkTitle,
  name,
  overline,
  overlineColor,
  overlineStyle,
  parallax,
  promotionTracking,
  richText,
  richTextBelowTargets,
  richTextBelowTargetsColor,
  richTextColor,
  subtitle,
  subtitleColor,
  subtitleStyle,
  targets = [],
  textAlignmentLarge,
  textAlignmentSmall,
  title,
  titleColor,
  titleH1,
  titleStyle,
} = content

countdown && provide(DynamicFieldsKey, { countdown })

const { fetchpriority, lazy } = inject(SectionContextKey, {} as SectionProvider)
const { elementRef, handlePromotionClick } = usePromotionTracking(promotionTracking, name)

const videoRef = ref()
const mediaLink = mediaTarget || targets[0]
const responsiveMedia = getResponsiveMedia(media)
const aspectRatio = responsiveMedia?.aspectRatio || {}

const contentPane = Object.keys(aspectRatio).reduce((acc, cur) => {
  acc[cur] = `calc(1 / ${aspectRatio[cur]} * 100%)`
  return acc
}, {})

const parallaxOffset = parallax?.config.innerOffset
const parallaxRef = useTemplateRef('parallaxRef')
const { config: parallaxConfig, translate: parallaxTranslate } = getOffsetParallaxConfig(parallaxOffset, {
  0: 0,
  1: 50,
  2: 70,
  3: 85,
  4: 100,
  5: 125,
})

if (parallaxConfig) {
  useParallaxController(parallaxRef, parallaxConfig)
}

const percentHeightHeaderOffsetPx = useHydrationSafeHeaderOffsetPx()

const usesPercentHeightSmMd = hasPercentHeightAtBreakpoint(componentPercentHeight?.small)
const usesPercentHeightLg = hasPercentHeightAtBreakpoint(componentPercentHeight?.large)
const usesPercentHeight = usesPercentHeightSmMd || usesPercentHeightLg
const anchorSmall = componentPercentHeight?.anchorSmall
const anchorLarge = componentPercentHeight?.anchorLarge

const percentHeightStyle = computed(() =>
  getPercentHeightCssVars(componentPercentHeight, percentHeightHeaderOffsetPx.value),
)

const objectPosition = getResponsiveObjectPosition(anchorSmall, anchorLarge)

const { hasCaption, captionProps } = getCaptionConfig(content)

const textAlignmentSmallClasses: Record<TextAlignment, string> = {
  left: '<md:items-start <md:text-left',
  center: '<md:items-center <md:text-center',
  right: '<md:items-end <md:justify-end <md:text-right'
}

const textAlignmentLargeClasses: Record<TextAlignment, string> = {
  left: 'md:items-start md:text-left',
  center: 'md:items-center md:text-center',
  right: 'md:items-end md:justify-end md:text-right'
}

const contentAlignment = [
  textAlignmentSmallClasses[textAlignmentSmall],
  textAlignmentLargeClasses[textAlignmentLarge],
]

const titleTag = computed(() => titleH1
  ? 'h1'
  : isPartOfCollection
    ? parentHeadingLevel ? `h${parentHeadingLevel + 1}` : firstElement && index === 0 ? 'h1' : 'h2'
    : firstElement ? 'h1' : 'h2')

const roundedCornersClasses = getRoundedCorners(content, componentCorners)

useAutoplay(videoRef)
</script>
